import React from 'react';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import BubbleContent from '../content/bubble_content';
import ConversationItem from 'models/conversation_item';
import InlineAnnotatedContentContainer from 'components/lib/inline_annotated_content_container';
import RedactedItem from 'components/customer/conversation_history/conversation_items_v2/redacted_item';
import VisuallyHidden from 'components/common/visually_hidden';

const AI_SOURCE = 'AI';
const STATIC_SOURCE = 'STATIC';

export default function ChatButtonPromptResponseContent({ className, item, status }) {
  const { t } = useTranslation();
  const { isRedacted, messageData, text } = item.content;
  const offeredPrompts = (messageData && messageData.offeredPrompts) || [];
  const promptsSource = messageData && messageData.promptsSource;

  let sourceLabel = null;
  if (promptsSource === AI_SOURCE) {
    sourceLabel = t('AI-generated prompts');
  } else if (promptsSource === STATIC_SOURCE) {
    sourceLabel = t('Preset prompts');
  }

  let body;
  if (isRedacted) {
    body = <RedactedItem />;
  } else if (!offeredPrompts.length) {
    // Malformed payload — degrade to the selected prompt text (spec §8.1) rather than crash the timeline.
    body = <InlineAnnotatedContentContainer id={item.id} text={text} />;
  } else {
    body = (
      <React.Fragment>
        {sourceLabel && (
          <SourceMarker data-aid="button-prompt-source" data-source={promptsSource}>
            {sourceLabel}
          </SourceMarker>
        )}
        <PromptList data-aid="button-prompt-list" role="list">
          {offeredPrompts.map((prompt, index) => {
            const isSelected = prompt.id != null && prompt.id === messageData.selectedPromptId;
            return (
              <Prompt
                $isSelected={isSelected}
                aria-current={isSelected || undefined}
                data-aid={`button-prompt-${prompt.id}`}
                data-selected={isSelected}
                key={`${prompt.id}-${index}`}
              >
                {isSelected && <VisuallyHidden>{t('Selected')}</VisuallyHidden>}
                {prompt.text}
              </Prompt>
            );
          })}
        </PromptList>
      </React.Fragment>
    );
  }

  return (
    <BubbleContent
      className={classnames('chatItem-content button-prompt-response', className)}
      data-aid={`chatItem-button-prompt-response-${item.id}`}
      item={item}
      status={status}
    >
      {body}
    </BubbleContent>
  );
}

ChatButtonPromptResponseContent.propTypes = {
  className: PropTypes.string,
  item: PropTypes.instanceOf(ConversationItem).isRequired,
  status: PropTypes.string,
};

const SourceMarker = styled.div`
  color: ${p => p.theme.colors.gray600};
  font-size: ${p => p.theme.fontSize.small};
  font-weight: ${p => p.theme.fontWeight.mediumHeavy};
  margin-bottom: ${p => p.theme.spacing.stackSmall};
`;

const PromptList = styled.ul`
  display: flex;
  flex-direction: column;
  gap: ${p => p.theme.spacing.stackXSmall};

  && {
    list-style: none;
    margin: 0;
    padding: 0;
  }
`;

const Prompt = styled.li`
  background: ${p => (p.$isSelected ? p.theme.colors.purple100 : 'transparent')};
  border: 1px solid ${p => (p.$isSelected ? p.theme.colors.purple200 : 'transparent')};
  border-radius: ${p => p.theme.borderRadius.small};
  color: ${p => (p.$isSelected ? p.theme.colors.defaultFontBlack : p.theme.colors.gray700)};
  font-weight: ${p => (p.$isSelected ? p.theme.fontWeight.mediumHeavy : p.theme.fontWeight.normal)};
  padding: ${p => p.theme.spacing.insetSmall};
`;
